'use client';

import { useClientConfig } from '@shared/utils';
import {
  type InfiniteData,
  type Updater,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  useSuspenseQuery,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useApiService, useOnUnauthorised } from '../providers/ApiServiceProvider';
import { ApiError } from './apiError';
import { type ApiRequestOptions, type ApiResponse, HTTP_METHODS, METHODS_WITH_BODY, type MethodsWithBody } from './service';

const HTTP_STATUS_UNAUTHORISED = 401;

// Query key constant
const QUERY_KEY_PREFIX = 'api';

// Base query key factory for consistent cache key management
const createQueryKey = (path: string, params?: Record<string, string | number | boolean | string[]>) => {
  const baseKey = [QUERY_KEY_PREFIX, path];
  return params ? [...baseKey, params] : baseKey;
};

// Helper function to merge headers with automatic API key inclusion
const mergeHeadersWithApiKey = (defaultApiKey: string, userHeaders?: Record<string, string>, method = 'GET', body?: unknown): Record<string, string> => {
  const { apiKey = defaultApiKey, ...headersWithoutApiKey } = userHeaders || {};
  const baseHeaders: Record<string, string> = {};

  // Add user headers first
  if (Object.keys(headersWithoutApiKey).length) {
    Object.assign(baseHeaders, headersWithoutApiKey);
  }

  // Add x-api-key header if API key is available and is not already set
  if (apiKey && !baseHeaders['x-api-key']) {
    baseHeaders['x-api-key'] = apiKey;
  }

  // Only set default JSON content-type if there's a body and it's not FormData
  if (METHODS_WITH_BODY.includes(method as MethodsWithBody) && !baseHeaders['Content-Type'] && body && !(body instanceof FormData)) {
    baseHeaders['Content-Type'] = 'application/json';
  }

  return baseHeaders;
};

const normalizeCredentials = (credentials?: boolean | RequestCredentials): RequestCredentials => {
  if (typeof credentials === 'boolean') {
    return credentials ? 'include' : 'omit';
  }
  return credentials || 'same-origin';
};

// Helper function to check if a URL is absolute (third-party API)
const isAbsoluteUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

// Helper function to build URL with query parameters for absolute URLs
const buildUrlWithParams = (url: string, params?: Record<string, string | number | boolean | string[]>): string => {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Handle array parameters by adding multiple entries with the same key
      value.forEach(item => searchParams.append(key, String(item)));
    } else {
      searchParams.append(key, String(value));
    }
  });

  return `${url}${url.includes('?') ? '&' : '?'}${searchParams.toString()}`;
};

// Extended options interface for hooks with convenient credentials handling
type HookOptions = {
  credentials?: boolean | RequestCredentials; // Allow boolean for convenience
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | string[]>;
  redirect?: RequestRedirect;
  /** Fetch cache mode, e.g. `'no-store'` for an endpoint the page polls but the server lets browsers cache. */
  cache?: RequestCache;
  /** Lets the request outlive the page, e.g. fire-and-forget telemetry sent just before a full navigation. */
  keepalive?: boolean;
  returnNullOn404?: boolean;
  returnNullOn400?: boolean;
  responseType?: 'json' | 'text';
  body?: unknown;
};

// Helper to execute an ApiService request by method
const executeApiServiceRequest = async <TData>(
  apiService: ReturnType<typeof useApiService>,
  path: string,
  method: (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS],
  body: unknown,
  requestOptions: ApiRequestOptions
): Promise<ApiResponse<TData>> => {
  switch (method) {
    case HTTP_METHODS.GET:
      return apiService.get<TData>(path, requestOptions);
    case HTTP_METHODS.POST:
      return apiService.post<TData>(path, body, requestOptions);
    case HTTP_METHODS.PUT:
      return apiService.put<TData>(path, body, requestOptions);
    case HTTP_METHODS.PATCH:
      return apiService.patch<TData>(path, body, requestOptions);
    case HTTP_METHODS.DELETE:
      return apiService.delete<TData>(path, requestOptions);
    default:
      throw new Error(`Unsupported method: ${method}`);
  }
};

// Hook-based API call function for both local and third-party APIs
const useApiCall = () => {
  const apiService = useApiService();
  const { apiKey } = useClientConfig();
  const onUnauthorised = useOnUnauthorised();

  return async <TData>(path: string, method: (typeof HTTP_METHODS)[keyof typeof HTTP_METHODS], options: HookOptions): Promise<TData | null> => {
    const { credentials, params, headers, body, redirect, cache, keepalive, returnNullOn404, returnNullOn400, responseType = 'json' } = options;
    const mergedHeaders = mergeHeadersWithApiKey(apiKey, headers, method, body);

    // For absolute URLs (third-party APIs), use native fetch
    if (isAbsoluteUrl(path)) {
      const url = method === HTTP_METHODS.GET ? buildUrlWithParams(path, params) : path;

      const fetchOptions: RequestInit = {
        method,
        credentials: normalizeCredentials(credentials),
        headers: mergedHeaders,
        body: METHODS_WITH_BODY.includes(method as MethodsWithBody) && body ? JSON.stringify(body) : undefined,
        redirect,
        ...(cache ? { cache } : {}),
        ...(keepalive ? { keepalive } : {}),
      };

      let response = await fetch(url, fetchOptions);

      // Handle 401 — attempt token refresh and retry once
      if (response.status === HTTP_STATUS_UNAUTHORISED && onUnauthorised) {
        const shouldRetry = await onUnauthorised();
        if (shouldRetry) {
          response = await fetch(url, fetchOptions);
        }
      }

      if (!response.ok) {
        // Allow callers to opt-in to resolving null on 404 instead of throwing
        if (returnNullOn404 && response.status === 404) {
          return null as unknown as TData;
        }
        if (returnNullOn400 && response.status === 400) {
          return null as unknown as TData;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return responseType === 'text' ? (response.text() as Promise<TData>) : response.json();
    }

    // For relative URLs (local APIs), use context-based ApiService
    const requestOptions: ApiRequestOptions = {
      params,
      credentials: normalizeCredentials(credentials),
      headers: mergedHeaders,
      redirect,
      ...(cache ? { cache } : {}),
      ...(keepalive ? { keepalive } : {}),
    };

    let response = await executeApiServiceRequest<TData>(apiService, path, method, body, requestOptions);

    // Handle 401 — attempt token refresh and retry once
    if (response.status === HTTP_STATUS_UNAUTHORISED && response.error && onUnauthorised) {
      const shouldRetry = await onUnauthorised();
      if (shouldRetry) {
        response = await executeApiServiceRequest<TData>(apiService, path, method, body, requestOptions);
      }
    }

    if (response.error) {
      // Allow callers to opt-in to resolving null on 404 instead of throwing
      if (returnNullOn404 && response.error instanceof ApiError && response.status === 404) {
        return null as unknown as TData;
      }
      if (returnNullOn400 && response.error instanceof ApiError && response.status === 400) {
        return null as unknown as TData;
      }
      throw response.error;
    }

    // Allow null data for successful responses (e.g., endpoints that return 200 OK with no body)
    return response.data;
  };
};

// Public options type for useApiQuery consumers
type UseApiQueryOptions<TQueryFnData, TData = TQueryFnData | null> = HookOptions & {
  queryOptions?: Omit<UseQueryOptions<TQueryFnData | null, Error, TData>, 'queryKey'>;
};

/**
 * GET Hook - useApiQuery
 *
 * Standard React Query hook for fetching data
 */
function useApiQuery<TQueryFnData = unknown, TData = TQueryFnData | null>(path: string, options?: UseApiQueryOptions<TQueryFnData, TData>) {
  const { params, credentials, queryOptions, headers, returnNullOn404, responseType, ...otherOptions } = options || {};
  const apiCall = useApiCall();

  const defaultQueryFn = async (): Promise<TQueryFnData | null> => {
    return apiCall<TQueryFnData>(path, HTTP_METHODS.GET, {
      credentials,
      params,
      headers,
      returnNullOn404,
      responseType,
      ...otherOptions,
    });
  };

  return useQuery({
    queryKey: createQueryKey(path, params),
    queryFn: queryOptions?.queryFn ?? defaultQueryFn,
    ...queryOptions,
  });
}

/**
 * Suspense GET Hook - useApiSuspenseQuery
 *
 * Suspense mode enables the use of the Suspense component for loading states
 */
function useApiSuspenseQuery<TQueryFnData = unknown, TData = TQueryFnData | null>(
  path: string,
  options?: HookOptions & {
    queryOptions?: Omit<UseSuspenseQueryOptions<TQueryFnData | null, Error, TData>, 'queryKey' | 'queryFn'>;
  }
) {
  const { params, credentials, queryOptions, headers, returnNullOn404, responseType, ...otherOptions } = options || {};
  const apiCall = useApiCall();

  const queryConfig = {
    queryKey: createQueryKey(path, params),
    queryFn: async (): Promise<TQueryFnData | null> => {
      return apiCall<TQueryFnData>(path, HTTP_METHODS.GET, {
        credentials,
        params,
        headers,
        returnNullOn404,
        responseType,
        ...otherOptions,
      });
    },
    ...queryOptions,
  };

  return useSuspenseQuery(queryConfig);
}

/**
 * Infinite Query Hook - useApiInfiniteQuery
 *
 * React Query infinite query hook for paginated data fetching
 */
function useApiInfiniteQuery<TData = unknown, TPageParam = number>(
  path: string,
  options: HookOptions & {
    queryOptions?: Omit<
      UseInfiniteQueryOptions<TData | null, Error, InfiniteData<TData | null, TPageParam>, readonly unknown[], TPageParam>,
      'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
    >;
    getNextPageParam: (lastPage: TData | null, allPages: (TData | null)[]) => TPageParam | undefined;
    initialPageParam?: TPageParam;
    pageParamKey?: string; // Query param key used for the page param value. Default: `'pageNumber'`
  }
): UseInfiniteQueryResult<InfiniteData<TData | null, TPageParam>, Error> {
  const { params, credentials, queryOptions, headers, returnNullOn404, responseType, getNextPageParam, initialPageParam, pageParamKey = 'pageNumber', ...otherOptions } = options;
  const apiCall = useApiCall();

  return useInfiniteQuery<TData | null, Error, InfiniteData<TData | null, TPageParam>, readonly unknown[], TPageParam>({
    queryKey: createQueryKey(path, params),
    queryFn: async context => {
      const pageParam = context.pageParam;
      const pageParams = { ...params, [pageParamKey]: pageParam };
      return apiCall<TData>(path, HTTP_METHODS.GET, {
        credentials,
        params: pageParams as Record<string, string | number | boolean | string[]>,
        headers,
        returnNullOn404,
        responseType,
        ...otherOptions,
      });
    },
    getNextPageParam,
    initialPageParam: (initialPageParam ?? 0) as TPageParam,
    ...queryOptions,
  });
}

/**
 * GET Hook with Headers - useApiQueryWithHeaders
 * Returns both data and response headers as separate objects
 */
function useApiQueryWithHeaders<TData = unknown>(
  path: string,
  options?: HookOptions & {
    queryOptions?: Omit<
      UseQueryOptions<{ data: TData | null; headers: Headers; status?: number }, Error, { data: TData | null; headers: Headers; status?: number }>,
      'queryKey' | 'queryFn' | 'select'
    >;
  }
) {
  const { params, credentials, queryOptions, headers } = options || {};
  const apiService = useApiService();
  const { apiKey } = useClientConfig();

  const query = useQuery({
    queryKey: createQueryKey(path, params),
    queryFn: async (): Promise<{ data: TData | null; headers: Headers; status?: number }> => {
      const requestOptions: ApiRequestOptions = {
        params,
        credentials: normalizeCredentials(credentials),
        headers: mergeHeadersWithApiKey(apiKey, headers, 'GET', options?.body),
      };

      const response = await apiService.get<TData>(path, requestOptions);

      if (response.error) {
        throw response.error;
      }

      // For 204 No Content responses, data can be null but we still want to return headers
      if (response.data === null && response.status !== 204) {
        throw new Error('No data received from API');
      }

      return {
        data: response.data,
        headers: response.headers || new Headers(),
        status: response.status,
      };
    },
    ...queryOptions,
  });

  // Return separate data, headers, and status objects
  return {
    ...query,
    data: query.data?.data,
    headers: query.data?.headers,
    status: query.data?.status,
  };
}

/**
 * Generic Mutation Hook - useApiMutation
 */
function useApiMutation<TData = unknown, TVariables = unknown>(
  path: string,
  method: typeof HTTP_METHODS.POST | typeof HTTP_METHODS.PUT | typeof HTTP_METHODS.PATCH | typeof HTTP_METHODS.DELETE,
  options?: {
    mutationOptions?: Omit<UseMutationOptions<TData | null, Error, TVariables>, 'mutationFn'>;
    requestOptions?: HookOptions;
    minimumDelayMs?: number;
  }
) {
  const { mutationOptions, requestOptions = {}, minimumDelayMs } = options || {};
  const { credentials, headers, ...otherRequestOptions } = requestOptions;
  const apiCall = useApiCall();

  return useMutation({
    ...mutationOptions,
    mutationFn: async (variables: TVariables): Promise<TData | null> => {
      const start = Date.now();
      const result = await apiCall<TData>(path, method, {
        credentials,
        headers,
        body: variables,
        ...otherRequestOptions,
      });
      if (minimumDelayMs) {
        await new Promise(res => setTimeout(res, minimumDelayMs - (Date.now() - start)));
      }
      return result;
    },
  });
}

/**
 * Prefetch Hook - useApiPrefetch
 */
function useApiPrefetch() {
  const queryClient = useQueryClient();
  const apiCall = useApiCall();
  const apiCallRef = useRef(apiCall);

  // Synced in a layout effect (not during render) so it's current before any passive effect in the same commit calls `prefetch`.
  useLayoutEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  return useMemo(
    () => ({
      prefetch: <TData = unknown>(
        path: string,
        options?: HookOptions & {
          staleTime?: number;
        }
      ) => {
        const { params, credentials, staleTime = 5 * 60 * 1000, headers, ...otherOptions } = options || {};

        return queryClient.prefetchQuery({
          queryKey: createQueryKey(path, params),
          queryFn: async (): Promise<TData | null> => {
            return apiCallRef.current<TData>(path, 'GET', {
              credentials,
              params,
              headers,
              ...otherOptions,
            });
          },
          staleTime,
        });
      },
    }),
    [queryClient]
  );
}

const matchesQueryPathPrefix = (queryKey: readonly unknown[], pathPrefix: string): boolean => {
  if (!Array.isArray(queryKey) || queryKey.length < 2) {
    return false;
  }
  const [prefix, path] = queryKey;
  return prefix === QUERY_KEY_PREFIX && typeof path === 'string' && path.startsWith(pathPrefix);
};

/**
 * Invalidate Queries Hook - useApiInvalidate
 *
 * Helper hook to invalidate queries without importing useQueryClient in each component
 */
function useApiInvalidate() {
  const queryClient = useQueryClient();

  return {
    invalidateQuery: (path: string, params?: Record<string, string | number | boolean | string[]>) => {
      return queryClient.invalidateQueries({
        queryKey: createQueryKey(path, params),
      });
    },

    invalidateQueriesStartingWith: (pathPrefix: string) => {
      return queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_PREFIX, pathPrefix],
        exact: false,
      });
    },

    invalidateAllQueries: () => {
      return queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_PREFIX],
      });
    },

    refetchQueriesStartingWith: (pathPrefix: string) => {
      return queryClient.refetchQueries({
        predicate: query => matchesQueryPathPrefix(query.queryKey, pathPrefix),
      });
    },

    invalidateQueriesMatchingPath: (pathPrefix: string) => {
      return queryClient.invalidateQueries({
        predicate: query => matchesQueryPathPrefix(query.queryKey, pathPrefix),
      });
    },
  };
}

/**
 * Cache Helper Hook - useApiCache
 *
 * Helper hook to clear, cancel, or read cached data without importing useQueryClient in each component
 */
function useApiCacheControl() {
  const queryClient = useQueryClient();

  return {
    cancelQuery: (path: string, params?: Record<string, string | number | boolean | string[]>) =>
      queryClient.cancelQueries({ queryKey: createQueryKey(path, params), exact: true }),

    setQueryData: <T = unknown>(path: string, params: Record<string, string | number | boolean | string[]> | undefined, updater: Updater<T | undefined, T | undefined>) =>
      queryClient.setQueryData<T | undefined>(createQueryKey(path, params), updater),

    resetQueryData: (path: string, params?: Record<string, string | number | boolean | string[]>) =>
      queryClient.resetQueries({ queryKey: createQueryKey(path, params), exact: true }),

    getQueryData: <T = unknown>(path: string, params?: Record<string, string | number | boolean | string[]>) => queryClient.getQueryData<T>(createQueryKey(path, params)),
  };
}

// Export all declarations at the end of the file
export {
  createQueryKey,
  QUERY_KEY_PREFIX,
  useApiCacheControl,
  useApiCall,
  useApiInfiniteQuery,
  useApiInvalidate,
  useApiMutation,
  useApiPrefetch,
  useApiQuery,
  useApiQueryWithHeaders,
  useApiSuspenseQuery,
};
export type { HookOptions, UseApiQueryOptions };
